/**
 * Mobile artworks sheet - displays artwork list and upload in a bottom sheet
 */

import { Sheet, SheetContent, cn } from '@frameit/ui';
import { useProjectStore, getLocalImageUrl } from '../../stores/projectStore';
import { Uploader } from '../upload/Uploader';
import { useArtworkUpload } from '../../hooks/useArtworkUpload';

export function MobileArtworksSheet() {
  const project = useProjectStore((s) => s.project);
  const selectedArtworkId = useProjectStore((s) => s.selectedArtworkId);
  const selectArtwork = useProjectStore((s) => s.selectArtwork);
  const removeArtwork = useProjectStore((s) => s.removeArtwork);
  const saveToHistory = useProjectStore((s) => s.saveToHistory);
  const addArtwork = useProjectStore((s) => s.addArtwork);
  const mobileArtworksOpen = useProjectStore((s) => s.mobileArtworksOpen);
  const setMobileArtworksOpen = useProjectStore((s) => s.setMobileArtworksOpen);
  const { upload: uploadToApi } = useArtworkUpload();

  // Handle artwork upload — local preview + background API sync
  const handleUpload = (file: File, dimensions: { width: number; height: number }): void => {
    saveToHistory();
    uploadToApi(file, dimensions).then((result) => {
      addArtwork(result.artworkId, {
        templateId: 'frame-001',
        width: 25,
      });
      setMobileArtworksOpen(false);
    });
  };

  return (
    <Sheet open={mobileArtworksOpen} onOpenChange={setMobileArtworksOpen} defaultSnapPoint={60}>
      <SheetContent showOverlay={true} draggable={true}>
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 pb-3">
          <h3 className="text-lg font-semibold">Artworks</h3>
          <span className="text-sm text-muted-foreground">
            {project?.framedArtworks?.length ?? 0} items
          </span>
        </div>

        {/* Upload section */}
        <div className="border-b p-4">
          <Uploader onUpload={handleUpload} allowCamera />
        </div>

        {/* Artwork grid */}
        <div className="flex-1 overflow-auto p-4">
          {project?.framedArtworks && project.framedArtworks.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {project.framedArtworks.map((artwork) => {
                const imageUrl = getLocalImageUrl(artwork.artworkId);
                const isSelected = selectedArtworkId === artwork.id;

                return (
                  <div
                    key={artwork.id}
                    className={cn(
                      'relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all',
                      isSelected
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-transparent hover:border-muted-foreground/50',
                    )}
                    onClick={() => {
                      selectArtwork(artwork.id);
                      setMobileArtworksOpen(false);
                    }}
                  >
                    {imageUrl ? (
                      <img src={imageUrl} alt="Artwork" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-muted">
                        <span className="text-xs text-muted-foreground">No image</span>
                      </div>
                    )}

                    {/* Delete button */}
                    <button
                      className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 hover:bg-destructive transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        saveToHistory();
                        removeArtwork(artwork.id);
                      }}
                    >
                      <svg
                        className="h-4 w-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>

                    {/* Selected indicator */}
                    {isSelected && (
                      <div className="absolute bottom-1 left-1 rounded bg-primary px-1.5 py-0.5 text-[10px] text-primary-foreground">
                        Selected
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Upload an image to get started
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
